import moment from 'moment';
import { DemographicsTypeId, MAHDemographicsTypeId } from 'core/enums/genex';
import { SitePlatform } from 'core/enums/site-platform';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { Demographic, ModelWithCategory, OptionalUserData, Store } from './interfaces';

type DemographicsModel = ModelWithCategory & {
  userData?: OptionalUserData;
  customerNumber: string;
  eventDate: string;
  eventDateTime: string;
  action: string;
  brand: string;
  highestPrincipalPackage: string;
  eventDescription?: string;
  surveyName?: string;
  userEmail?: string;
}

export const getFullDemographicsModel = (store: Store, model: ModelWithCategory, customerNumber: string, timestamp: Date) => {
  const { category } = model;

  const action: string = category.toString();
  const brand = SiteConfigStore.platform() === SitePlatform.dstv ? 'DStv' : 'GOtv';

  const m = moment(timestamp);
  // const d = timestamp;
  // const month: number = d.getMonth() + 1;
  // const day: number = d.getDate();

  const dateFormat = store.useMAHDemographics ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY/MM/DD HH:mm';

  const eventDate: string = m.format('YYYY/MM/DD');
  const eventDateTime: string = m.format(dateFormat);

  // const uniqueInteractionId: string = new Date().getTime().toString();
  const userData = store.optionalUserData;

  const highestPrincipalPackage = userData?.highestPrincipalPackage ? userData.highestPrincipalPackage : '';

  const demographicsModel: DemographicsModel = {
    ...model,
    userData,
    customerNumber,
    eventDate,
    eventDateTime,
    action,
    brand,
    highestPrincipalPackage,
    userEmail: store.userEmail,
  };

  return demographicsModel;
};

//SA Customers
const getDemographics = (model: DemographicsModel) => {
  const {
    customerNumber,
    userData,
    eventDate,
    eventDateTime,
    eventDescription,
    action,
    outcome,
    language,
    brand,
    highestPrincipalPackage,
    userEmail,
  } = model;

  const _ = DemographicsTypeId;

  const demographics: [DemographicsTypeId, string][] = [
    [_.AgeOfAccount, ''],
    [_.AgeOfCustomer, ''],
    [_.Brand, brand],
    [_.Channel, ''],
    [_.CommunicationMethodDesc, ''],
    [_.Country, userData?.country ? userData.country : ''],
    [_.CustomerAccountStatus, ''],
    [_.CustomerAccountType, userData?.customerAccountType || ''],
    [_.CustomerClassDesc, ''],
    [_.CustomerEmailAddress, userEmail || ''],
    [_.CustomerName, 'Marketing'],
    [_.CustomerNumber, customerNumber],
    [_.CustomerPhoneNumber, userData?.phoneNumber ? userData.phoneNumber : ''],
    [_.CustomerRegion, userData?.country ? userData.country : ''],
    [_.CustomerStatusDesc, ''],
    [_.CustomerType, ''],
    [_.DecoderTypeUsed, ''],
    [_.EventDescription, ''],
    [_.EventNo, ''],
    [_.EventReasonDescription, eventDescription || ''],
    [_.FinancialProductName, ''],
    [_.FinancialProductType, ''],
    [_.HasUsedBoxOffice, ''],
    [_.HasUsedConnectedExplora, ''],
    [_.HasUsedDStvNow, ''],
    [_.HasUsedSuperSport, ''],
    [_.HighestPrincipalPackage, highestPrincipalPackage],
    [_.InstallerName, ''],
    [_.InteracteeName, userData?.userName ? userData.userName : ''],
    [_.InteracteeTelephoneNumber, ''],
    [_.InteractionAgentADAccountFullName, ''],
    [_.InteractionAgentAgencyName, ''],
    [_.InteractionAgentContactCentreManagerDesc, ''],
    [_.InteractionAgentDepartment, ''],
    [_.InteractionAgentDutyManagerDesc, ''],
    [_.InteractionAgentEmailAddress, ''],
    [_.InteractionAgentFullName, ''],
    [_.InteractionAgentSupervisorDesc, ''],
    [_.InteractionCategory, action],
    [_.InteractionEventChannel, 'WEB'],
    [_.InteractionEventDate, eventDate],
    [_.InteractionEventDateTime, eventDateTime],
    [_.InteractionEventFlag, ''],
    [_.InteractionEventID, eventDate],
    [_.InteractionEventTime, ''],
    [_.InteractionOutcome, outcome],
    [_.InteractionSubCategory, 'web-uni'],
    [_.Journey, 'HELP'],
    [_.MethodOfPaymentName, ''],
    [_.NextInvoiceDate, ''],
    [_.NoOfDCCPolicies, ''],
    [_.NoOfDecoders, ''],
    [_.OptOutMessage, ''],
    [_.PaymentPoint, ''],
    [_.PreferredLanguageDesc, language ? language : ''],
    [_.ScheduledEventDate, ''],
    [_.StatusBeforePay, ''],
    [_.SurveyName, ''],
    [_.TouchPoint, 'Self Service'],
    [_.Unknown, ''],
  ];

  return demographics;
};

const getMahDemographics = (model: DemographicsModel) => {
  const {
    customerNumber,
    userData,
    eventDateTime,
    eventDescription,
    language,
    brand,
    surveyName,
    highestPrincipalPackage,
    journeyName,
  } = model;

  const _ = MAHDemographicsTypeId;

  const channel = 'Website';
  const journey = journeyName || surveyName || '';

  const demographics: [MAHDemographicsTypeId, string][] = [
    [_.AgeOfAccount, ''],
    [_.AgeOfCustomer, ''],
    [_.Brand, brand],
    [_.Channel, channel],
    [_.CommunicationMethodDesc, channel],
    [_.Country, userData?.country || userData?.countryCode  || ''],
    [_.CustomerAccountStatus, ''],
    [_.CustomerAccountType, userData?.customerAccountType || ''],
    [_.CustomerClassDesc, ''],
    [_.CustomerEmailAddress, ''],
    [_.CustomerName, userData?.userName || ''],
    [_.CustomerNumber, customerNumber],
    [_.CustomerPhoneNumber, userData?.phoneNumber || ''],
    [_.CustomerRegion, userData?.country || ''],
    [_.CustomerStatusDesc, ''],
    [_.CustomerType, 'Residential'],
    [_.EventDescription, eventDescription || ''],
    [_.EventNo, ''],
    [_.EventReasonDescription, ''],
    [_.FinancialProductName, ''],
    [_.FinancialProductType, ''],
    [_.HighestPrincipalPackage, highestPrincipalPackage],
    [_.InstallerName, ''],
    [_.InteracteeName, userData?.userName || ''],
    [_.InteracteeTelephoneNumber, userData?.phoneNumber || ''],
    [_.InteractionAgentADAccountFullName, 'ServiceBot'],
    [_.InteractionAgentAgencyName, ''],
    [_.InteractionAgentContactCentreManagerDesc, ''],
    [_.InteractionAgentDepartment, ''],
    [_.InteractionAgentDutyManagerDesc, ''],
    [_.InteractionAgentEmailAddress, ''],
    [_.InteractionAgentFullName, ''],
    [_.InteractionAgentSupervisorDesc, ''],
    [_.InteractionCategory, channel],
    [_.InteractionEventChannel, channel],
    [_.InteractionEventDate, eventDateTime],
    [_.InteractionEventDateTime, eventDateTime],
    [_.InteractionEventFlag, ''],
    [_.InteractionEventID, ''],
    [_.InteractionEventTime, ''],
    [_.InteractionOutcome, userData?.smartCardNumber || ''],
    [_.InteractionSubCategory, 'Survey'],
    [_.Journey, journey],
    [_.MAHRegion, ''],
    [_.MethodOfPaymentName, ''],
    [_.NextInvoiceDate, ''],
    [_.NoOfDCCPolicies, ''],
    [_.NoOfDecoders, ''],
    [_.OptOutMessage, ''],
    [_.PaymentPoint, ''],
    [_.PreferredLanguageDesc, language || ''],
    [_.ScheduledEventDate, eventDateTime],
    [_.StatusBeforePay, ''],
    [_.SurveyName, surveyName || ''],
    [_.TouchPoint, channel],
  ];

  return demographics;
};

const getDemographicsPayload = (store: Store, model: ModelWithCategory, customerNumber: string, timestamp: Date) => {
  const fullModel = getFullDemographicsModel(store, model, customerNumber, timestamp);

  const demographicsToUse = store.useMAHDemographics
    ? getMahDemographics(fullModel)
    : getDemographics(fullModel);

  const demographics: Demographic[] = demographicsToUse
    .sort(
      (a, b) => a[0] - b[0],
    )
    .map((pair) => ({
      demographics_type_id: pair[0],
      demographics_value: pair[1],
    }));

  return {
    import: true,
    demographics,
  };
};

export default getDemographicsPayload;